import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PageTitle } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, CalendarClock, Plane, Cake, Coffee, Clock, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";

const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const LEAVE_TYPES = ["leave", "off", "leave_pending"];

export default function CalendarPage() {
  const now = new Date();
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() }); // m 0-indexed
  const [events, setEvents] = useState([]);
  const [team, setTeam] = useState("all");
  const [person, setPerson] = useState("all");

  const monthStr = `${ym.y}-${String(ym.m + 1).padStart(2, "0")}`;
  useEffect(() => { api.get(`/calendar?month=${monthStr}`).then((r) => setEvents(r.data.events)).catch(() => {}); }, [monthStr]);

  // Filter option lists derived from leave events in view
  const teams = useMemo(() => [...new Set(events.filter((e) => LEAVE_TYPES.includes(e.type) && e.department).map((e) => e.department))].sort(), [events]);
  const people = useMemo(() => {
    const map = {};
    events.filter((e) => LEAVE_TYPES.includes(e.type) && e.employee_id).forEach((e) => { map[e.employee_id] = e.employee_name; });
    return Object.entries(map).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [events]);

  const matchesFilter = (e) => {
    if (!LEAVE_TYPES.includes(e.type)) return true; // holidays & birthdays always show
    if (team !== "all" && e.department !== team) return false;
    if (person !== "all" && e.employee_id !== person) return false;
    return true;
  };

  const first = new Date(ym.y, ym.m, 1);
  const startOffset = (first.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(ym.y, ym.m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const evFor = (d) => {
    const ds = `${monthStr}-${String(d).padStart(2, "0")}`;
    return events.filter((e) => (e.type === "holiday" || e.type === "birthday" ? e.date === ds : (e.date <= ds && (e.end_date || e.date) >= ds))).filter(matchesFilter);
  };
  // Clash = 2+ distinct employees on any leave (approved or pending) the same day
  const clashCount = (evs) => new Set(evs.filter((e) => LEAVE_TYPES.includes(e.type) && e.employee_id).map((e) => e.employee_id)).size;

  const move = (delta) => setYm((p) => { const nm = p.m + delta; return { y: p.y + Math.floor(nm / 12), m: ((nm % 12) + 12) % 12 }; });
  const label = first.toLocaleDateString("en-SG", { month: "long", year: "numeric" });
  const todayStr = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Singapore" });

  return (
    <div className="animate-fade-up">
      <PageTitle overline="Schedule · SGT" title="Team Calendar">
        <Button variant="outline" size="icon" onClick={() => move(-1)} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
        <span className="font-heading font-bold text-lg w-40 text-center">{label}</span>
        <Button variant="outline" size="icon" onClick={() => move(1)} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
      </PageTitle>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Select value={team} onValueChange={(v) => { setTeam(v); setPerson("all"); }}>
          <SelectTrigger className="h-9 w-52" data-testid="cal-team-filter"><SelectValue placeholder="All teams" /></SelectTrigger>
          <SelectContent className="bg-popover">
            <SelectItem value="all">All teams</SelectItem>
            {teams.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={person} onValueChange={setPerson}>
          <SelectTrigger className="h-9 w-56" data-testid="cal-person-filter"><SelectValue placeholder="All people" /></SelectTrigger>
          <SelectContent className="bg-popover max-h-72">
            <SelectItem value="all">All people</SelectItem>
            {people.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {(team !== "all" || person !== "all") && <Button variant="ghost" size="sm" onClick={() => { setTeam("all"); setPerson("all"); }} data-testid="cal-clear-filters">Clear</Button>}
      </div>

      <div className="flex flex-wrap gap-4 mb-4 text-sm">
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-destructive/70" /> Public Holiday</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-primary/60" /> Approved Leave</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded border border-dashed border-warning bg-warning/20" /> Pending Leave</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-warning/70" /> Off-in-lieu</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-pink-500/70" /> Birthday</span>
        <span className="inline-flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5 text-destructive" /> Clash (2+ off)</span>
      </div>

      <Card className="rounded-md border-border overflow-hidden">
        <div className="grid grid-cols-7 border-b border-border bg-muted/50">
          {DOW.map((d) => <div key={d} className="px-2 py-2 text-center overline">{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((d, i) => {
            const ds = d ? `${monthStr}-${String(d).padStart(2, "0")}` : null;
            const evs = d ? evFor(d) : [];
            const clash = clashCount(evs) >= 2;
            return (
              <div key={i} className={cn("min-h-[92px] border-b border-r border-border p-1.5 align-top relative", ds === todayStr && "bg-primary/5", clash && "bg-destructive/5 ring-1 ring-inset ring-destructive/30")} data-testid={d ? `cal-day-${d}` : undefined}>
                {d && (
                  <div className="flex items-center justify-between">
                    <span className={cn("text-xs font-mono-num", ds === todayStr ? "font-bold text-primary" : "text-muted-foreground")}>{d}</span>
                    {clash && <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold text-destructive" data-testid={`cal-clash-${d}`} title={`${clashCount(evs)} people on leave`}><AlertTriangle className="h-2.5 w-2.5" />{clashCount(evs)}</span>}
                  </div>
                )}
                <div className="space-y-1 mt-1">
                  {evs.slice(0, 3).map((e, j) => {
                    const style = e.type === "holiday" ? "bg-destructive/10 text-destructive"
                      : e.type === "birthday" ? "bg-pink-500/10 text-pink-600 dark:text-pink-400"
                      : e.type === "off" ? "bg-warning/10 text-warning"
                      : e.type === "leave_pending" ? "bg-warning/10 text-warning border border-dashed border-warning/60"
                      : "bg-primary/10 text-primary";
                    const Icon = e.type === "holiday" ? CalendarClock : e.type === "birthday" ? Cake : e.type === "off" ? Coffee : e.type === "leave_pending" ? Clock : Plane;
                    return (
                      <div key={j} className={cn("text-[10px] px-1 py-0.5 rounded truncate flex items-center gap-1", style)} title={`${e.title}${e.type === "leave_pending" ? " (Pending approval)" : ""}`}>
                        <Icon className="h-2.5 w-2.5 shrink-0" />
                        <span className="truncate">{e.title}</span>
                      </div>
                    );
                  })}
                  {evs.length > 3 && <span className="text-[10px] text-muted-foreground">+{evs.length - 3} more</span>}
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
